import React, { useEffect } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { numberMask } from '@helpers/inputmask'

/* ── inline style objects ── */
const styles = {
  iconWrap: {
    width: 64,
    height: 64,
    margin: '0 auto 24px',
    background: '#eff6ff',
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  iconDashedRing: {
    position: 'absolute',
    inset: -4,
    borderRadius: 24,
    border: '2px dashed rgba(37,99,235,0.15)',
    animation: 'otpSpin 20s linear infinite'
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    textAlign: 'center',
    letterSpacing: -0.3,
    marginBottom: 8
  },
  subtitle: {
    fontSize: 14,
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 1.5,
    marginBottom: 28
  },
  phone: {
    fontFamily: 'monospace',
    fontWeight: 700,
    color: '#0f172a',
    fontSize: 14,
    wordSpacing: '-0.5em'
  },
  digitsRow: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    marginBottom: 24
  },
  digit: {
    width: 68,
    height: 76,
    border: '2px solid #cbd5e1',
    borderRadius: 4,
    background: '#f8fafc',
    fontFamily: 'monospace',
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    textAlign: 'center',
    outline: 'none',
    transition: 'all 0.2s ease',
    caretColor: '#2563eb'
  },
  digitFilled: {
    borderColor: '#2563eb',
    background: '#eff6ff'
  },
  digitExpired: {
    borderColor: '#fca5a5',
    background: '#fef2f2',
    color: '#b91c1c',
    opacity: 0.6
  },
  timerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 24,
    fontSize: 13,
    color: '#94a3b8'
  },
  timerCount: {
    fontFamily: 'monospace',
    fontWeight: 700,
    color: '#2563eb',
    fontSize: 13
  },
  verifyBtn: {
    width: '100%',
    height: 52,
    border: 'none',
    borderRadius: 4,
    background: '#2563eb',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: 0.3,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8
  },
  verifyBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed'
  },
  resendRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    marginTop: 24
  },
  resendLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: '#2563eb',
    cursor: 'pointer',
    background: '#eff6ff',
    border: '1.5px solid transparent',
    borderRadius: 8,
    padding: '9px 16px',
    transition: 'all 0.25s ease'
  },
  resendLinkDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    color: '#94a3b8',
    background: '#f1f5f9',
    border: '1.5px solid transparent'
  },
  resendLinkHighlight: {
    color: '#fff',
    background: '#2563eb',
    border: '1.5px solid #2563eb',
    padding: '9px 16px',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
    animation: 'otpResendPulse 0.6s ease-out'
  },
  dividerDot: {
    width: 3,
    height: 3,
    borderRadius: '50%',
    background: '#e2e8f0',
    flexShrink: 0
  },
  expiredBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 10,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 20
  }
}

/* ── keyframe injection ── */
const KEYFRAMES_ID = 'otp-verify-keyframes'
function injectKeyframes() {
  if (document.getElementById(KEYFRAMES_ID)) return
  const sheet = document.createElement('style')
  sheet.id = KEYFRAMES_ID
  sheet.textContent = `
    @keyframes otpSpin { to { transform: rotate(360deg); } }
    @keyframes otpResendPulse {
      0% { transform: scale(0.95); opacity: 0.6; box-shadow: 0 0 0 0 rgba(37,99,235,0.4); }
      50% { transform: scale(1.04); opacity: 1; box-shadow: 0 0 0 8px rgba(37,99,235,0); }
      100% { transform: scale(1); opacity: 1; box-shadow: 0 2px 8px rgba(37,99,235,0.25); }
    }
    .otp-digit:focus {
      border-color: #2563eb !important;
      background: #fff !important;
      box-shadow: 0 0 0 4px rgba(37,99,235,0.15), 0 1px 2px rgba(0,0,0,0.04) !important;
      transform: translateY(-2px);
    }
    .otp-verify-btn:hover:not(:disabled) {
      background: #1d4ed8 !important;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(37,99,235,0.3);
    }
    .otp-resend-link:hover:not(:disabled) { color: #1d4ed8 !important; background: #dbeafe !important; transform: translateY(-1px); box-shadow: 0 2px 8px rgba(37,99,235,0.15); }
  `
  document.head.appendChild(sheet)
}

/* ── SVG icons ── */
const ClockIcon = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
)

const ShieldIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
    />
  </svg>
)

const SmsIcon = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
    />
  </svg>
)

const PhoneIcon = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
    />
  </svg>
)

const AlertIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
    />
  </svg>
)

/* ── Shared styles for phone entry page (exported for parent use) ── */
export const phoneEntryStyles = {
  iconWrap: styles.iconWrap,
  iconDashedRing: styles.iconDashedRing,
  title: styles.title,
  subtitle: styles.subtitle,
  verifyBtn: styles.verifyBtn,
  verifyBtnDisabled: styles.verifyBtnDisabled
}

/**
 * OTPVerificationView — shared presentational component for OTP verification step.
 * Renders the modern digit-input UI. All state and API logic lives in the parent.
 */
export const OTPVerificationView = ({
  phoneNumber,
  digits,
  onDigitChange,
  onDigitKeyDown,
  onDigitPaste,
  digitRefs,
  titleText,
  subtitleText,
  timerFormatted,
  timerExpired,
  expiredText,
  timerLabel,
  onVerify,
  verifying,
  verifyDisabled,
  verifyText,
  verifyingText,
  showSmsResend = true,
  showCallResend = true,
  onResendSms,
  onResendCall,
  resendDisabled,
  resendTimerText,
  smsLabel,
  callLabel,
  showReCaptcha,
  reCaptchaSiteKey,
  onCaptchaChange,
  showRetryOptions = true,
  onClose
}) => {
  useEffect(() => {
    injectKeyframes()
  }, [])

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <button
        type="button"
        className="close remove-modal-close"
        onClick={onClose}
        style={{ position: 'absolute', top: 12, right: 16, zIndex: 2 }}>
        <span aria-hidden="true">&times;</span>
      </button>

      <div style={{ padding: '40px 32px 32px' }}>
        {/* Icon */}
        <div style={styles.iconWrap}>
          <div style={styles.iconDashedRing} />
          <img
            alt=""
            src="https://dufzo4epsnvlh.cloudfront.net/image/icon-new/verify-icon.svg"
            style={{ width: 28, height: 28 }}
          />
        </div>

        {/* Title & subtitle */}
        <h2 style={styles.title}>{titleText || 'Verify your phone'}</h2>
        <p style={styles.subtitle}>
          {subtitleText || 'We sent a 4-digit code to'}
          <br />
          <span style={styles.phone}>{numberMask(phoneNumber)}</span>
        </p>

        {/* Expired banner */}
        {timerExpired && (
          <div style={styles.expiredBanner}>
            <AlertIcon />
            {expiredText || 'Code expired — request a new one below'}
          </div>
        )}

        {/* Digit inputs */}
        <div style={styles.digitsRow}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={digitRefs[i]}
              className="otp-digit"
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={timerExpired}
              onChange={(e) => onDigitChange(i, e.target.value)}
              onKeyDown={(e) => onDigitKeyDown(i, e)}
              onPaste={i === 0 ? onDigitPaste : undefined}
              style={{
                ...styles.digit,
                ...(digit ? styles.digitFilled : {}),
                ...(timerExpired ? styles.digitExpired : {})
              }}
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>

        {/* Timer */}
        {!timerExpired && (
          <div style={styles.timerRow}>
            <ClockIcon />
            <span>{timerLabel || 'Code expires in'}</span>
            <span style={styles.timerCount}>{timerFormatted}</span>
          </div>
        )}

        {/* reCAPTCHA */}
        {showReCaptcha && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <ReCAPTCHA sitekey={reCaptchaSiteKey} onChange={onCaptchaChange} />
          </div>
        )}

        {/* Verify button */}
        <button
          type="button"
          className="otp-verify-btn"
          disabled={verifyDisabled}
          onClick={onVerify}
          style={{
            ...styles.verifyBtn,
            ...(verifyDisabled ? styles.verifyBtnDisabled : {})
          }}>
          <ShieldIcon />
          {verifying ? verifyingText || 'Verifying...' : verifyText || 'Verify Code'}
          {verifying && (
            <img
              className="spinner"
              src="https://dufzo4epsnvlh.cloudfront.net/image/default.svg"
              alt=""
              style={{ width: 18, height: 18 }}
            />
          )}
        </button>

        {/* Resend row */}
        {showRetryOptions && (
          <div style={styles.resendRow}>
            {showSmsResend && (
              <button
                type="button"
                className="otp-resend-link"
                disabled={resendDisabled && !timerExpired}
                onClick={onResendSms}
                style={{
                  ...styles.resendLink,
                  ...(resendDisabled && !timerExpired ? styles.resendLinkDisabled : {}),
                  ...(timerExpired ? styles.resendLinkHighlight : {})
                }}>
                <SmsIcon />
                <span>
                  {resendDisabled && !timerExpired
                    ? `${smsLabel || 'Resend SMS'} ${resendTimerText || ''}`
                    : smsLabel || 'Resend SMS'}
                </span>
              </button>
            )}

            {showCallResend && (
              <React.Fragment>
                {showSmsResend && <span style={styles.dividerDot} />}
                <button
                  type="button"
                  className="otp-resend-link"
                  disabled={resendDisabled && !timerExpired}
                  onClick={onResendCall}
                  style={{
                    ...styles.resendLink,
                    ...(resendDisabled && !timerExpired ? styles.resendLinkDisabled : {}),
                    ...(timerExpired ? styles.resendLinkHighlight : {})
                  }}>
                  <PhoneIcon />
                  <span>{callLabel || 'Call me'}</span>
                </button>
              </React.Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
